import { z as zod } from "zod";
import { ZodSchema, ZodTypeAny } from "zod";
import { getUploadFileId } from "@/app/lib/common/get-image-id";
import { UploadFile } from "@/app/lib/generated/graphql-types";
import { MEDIA_REF_SCHEMA_TAG } from "@/app/features/shared/__to_be_integrated/types/forms/MediaRefSchema";
import { LINKED_SCHEMA_TAG } from "@/app/features/shared/__to_be_integrated/types/forms/LinkedRefSchema";

export function editableFromGraphQl<TEntity, TEditable>(entity: TEntity, schema: ZodSchema<TEditable>): TEditable {
    const sanitized = stripGraphQlTypenames(entity);
    console.log('editableFromGraphQl', entity, sanitized, schema.parse(sanitized));
    return schema.parse(sanitized);
}

export async function inputFromEditable<TEditable, TInput>(
    editable: TEditable,
    editableSchema: ZodSchema<TEditable>
): Promise<TInput> {
    const validatedEditable = editableSchema.parse(editable);
    const sanitized = stripGraphQlTypenames(validatedEditable);
    const mapped = await mapBySchemaForInput(sanitized, editableSchema, "");
    return mapped as TInput;
}

function stripGraphQlTypenames(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(stripGraphQlTypenames);
    }

    if (!value || typeof value !== "object") {
        return value;
    }

    const input = value as Record<string, unknown>;
    const output: Record<string, unknown> = {};

    Object.entries(input).forEach(([key, entry]) => {
        if (key === "__typename") return;
        output[key] = stripGraphQlTypenames(entry);
    });

    return output;
}

async function mapBySchemaForInput(value: unknown, schema: ZodTypeAny, path: string): Promise<unknown> {
    if (value === null || value === undefined) {
        return value;
    }

    if (isMediaRefSchema(schema)) {
        return mapMediaRefToUploadId(value, path);
    }

    if (isDocumentRefSchema(schema)) {
        return mapDocumentRefToDocumentId(value, path);
    }

    const unwrappedSchema = unwrapSchema(schema);

    if (unwrappedSchema instanceof zod.ZodObject) {
        const shape = unwrappedSchema.shape;
        const output: Record<string, unknown> = {};
        const inputObject = value as Record<string, unknown>;

        for (const key of Object.keys(shape)) {
            if (!(key in inputObject)) continue;
            output[key] = await mapBySchemaForInput(inputObject[key], shape[key], joinPath(path, key));
        }

        return output;
    }

    if (unwrappedSchema instanceof zod.ZodArray) {
        const inputArray = value as unknown[];
        return await Promise.all(
            inputArray.map((item, index) => mapBySchemaForInput(item, unwrappedSchema.element, joinPath(path, String(index))))
        );
    }

    if (unwrappedSchema instanceof zod.ZodDiscriminatedUnion) {
        const discriminator = unwrappedSchema.discriminator;
        const discriminatorValue = (value as Record<string, unknown>)[discriminator];
        const optionSchema = unwrappedSchema.optionsMap.get(discriminatorValue as string | number | boolean | null | undefined);

        if (optionSchema) {
            return mapBySchemaForInput(value, optionSchema, path);
        }

        return stripGraphQlTypenames(value);
    }

    if (unwrappedSchema instanceof zod.ZodUnion) {
        const matchingOption = unwrappedSchema.options.find((option: ZodTypeAny) => option.safeParse(value).success);

        if (matchingOption) {
            return mapBySchemaForInput(value, matchingOption, path);
        }

        return stripGraphQlTypenames(value);
    }

    return stripGraphQlTypenames(value);
}

function unwrapSchema(schema: ZodTypeAny): ZodTypeAny {
    let current = schema;

    while (true) {
        if (current instanceof zod.ZodOptional || current instanceof zod.ZodNullable) {
            current = current.unwrap();
            continue;
        }

        if (current instanceof zod.ZodDefault) {
            current = current.removeDefault();
            continue;
        }

        if (current instanceof zod.ZodEffects) {
            current = current.innerType();
            continue;
        }

        if (current instanceof zod.ZodCatch) {
            current = current.removeCatch();
            continue;
        }

        if (current instanceof zod.ZodBranded || current instanceof zod.ZodReadonly) {
            current = current.unwrap();
            continue;
        }

        break;
    }

    return current;
}

function isMediaRefSchema(schema: ZodTypeAny): boolean {
    return hasSchemaTag(schema, MEDIA_REF_SCHEMA_TAG);
}

function isDocumentRefSchema(schema: ZodTypeAny): boolean {
    return hasSchemaTag(schema, LINKED_SCHEMA_TAG);
}

function hasSchemaTag(schema: ZodTypeAny, tag: string): boolean {
    let current: ZodTypeAny | null = schema;

    while (current) {
        if (current.description === tag) {
            return true;
        }

        if (current instanceof zod.ZodOptional || current instanceof zod.ZodNullable) {
            current = current.unwrap();
            continue;
        }

        if (current instanceof zod.ZodDefault) {
            current = current.removeDefault();
            continue;
        }

        if (current instanceof zod.ZodEffects) {
            current = current.innerType();
            continue;
        }

        if (current instanceof zod.ZodCatch) {
            current = current.removeCatch();
            continue;
        }

        if (current instanceof zod.ZodBranded || current instanceof zod.ZodReadonly) {
            current = current.unwrap();
            continue;
        }

        break;
    }

    return false;
}

async function mapMediaRefToUploadId(value: unknown, path: string): Promise<string | null | undefined> {
    if (value === null || value === undefined) {
        return value;
    }

    if (!isUploadFileRef(value)) {
        throw new Error(`Expected UploadFile at "${path || "root"}", received non-upload media ref.`);
    }

    return await getUploadFileId(value as UploadFile);
}

function mapDocumentRefToDocumentId(value: unknown, path: string): string | null | undefined {
    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value === "string") {
        return value;
    }

    if (!hasDocumentId(value)) {
        throw new Error(`Expected document reference at "${path || "root"}", received value without documentId.`);
    }

    return value.documentId;
}

function isUploadFileRef(value: unknown): boolean {
    if (!value || typeof value !== "object") {
        return false;
    }

    const candidate = value as Record<string, unknown>;
    return typeof candidate.documentId === "string" && typeof candidate.url === "string";
}

function hasDocumentId(value: unknown): value is { documentId: string } {
    if (!value || typeof value !== "object") {
        return false;
    }

    const candidate = value as Record<string, unknown>;
    return typeof candidate.documentId === "string";
}

function joinPath(base: string, segment: string): string {
    if (!base) return segment;
    return `${base}.${segment}`;
}
